import {
  CheckCircleFilled,
  CloseCircleFilled,
  ArrowLeftOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { Alert, Button, Card, Divider, Flex, Typography } from "antd";
import React, { useContext, useMemo } from "react";
import AuthService from "../../../Services/auth.service";
import LoaderContext from "../../../Context/LoaderContext/Index";
import {
  InterviewSession,
  Question,
  QuestionAnswers,
} from "../../../Interface/Index";
import { JobApplication } from "../../../Entities/job-application.entity";

interface EvaluateProps {
  questions: Question[];
  isRoundFinished: boolean;
  questionRoundAnswers: QuestionAnswers[];
  currentInterviewSession: InterviewSession;
  appliedJob: JobApplication;
  handleEvaluation: () => void;
  onNavigateToQuestions?: () => void;
  isLastRound: boolean;
}

const ApplicationUnderReview = () => (
  <Card bordered={false} style={{ marginTop: "20px" }}>
    <Flex justify="center" align="center" vertical gap={16}>
      <img
        src="/assets/Review.svg"
        alt="review-icon"
        height={100}
        width={100}
      />
      <p>Your application is under review. Our team will contact you soon.</p>
    </Flex>
  </Card>
);

const Evaluate: React.FC<EvaluateProps> = ({
  handleEvaluation,
  questions,
  appliedJob,
  isRoundFinished,
  questionRoundAnswers,
  currentInterviewSession,
  onNavigateToQuestions,
  isLastRound,
}) => {
  const currentUser = AuthService.getInstance().getCurrentUser();
  const { isLoading } = useContext(LoaderContext);

  const formatUserName = (name?: string) =>
    name ? name.charAt(0).toUpperCase() + name.slice(1) : "";

  const userName = useMemo(
    () => formatUserName(currentUser?.firstName),
    [currentUser]
  );

  const isAnyOneQuestionAnswered = useMemo(
    () => questionRoundAnswers.some((answer) => answer.answer !== null),
    [questionRoundAnswers]
  );

  const answeredQuestionCount = useMemo(
    () =>
      questionRoundAnswers.filter(
        (answer: QuestionAnswers) =>
          answer.answer !== null || answer.fileUrl !== null
      ).length,
    [questionRoundAnswers]
  );

  const renderAlert = (
    icon: React.ReactNode,
    message: string,
    description: string,
    type: "success" | "error" | "info",
    className: string
  ) => (
    <Alert
      message={
        <>
          {icon}
          {message}
        </>
      }
      showIcon={false}
      description={description}
      type={type}
      className={className}
      style={{ maxWidth: "800px" }}
    />
  );

  // Success message for qualified applicants
  const renderSuccessMessage = () =>
    renderAlert(
      <CheckCircleFilled style={{ marginRight: 8, color: "#38BA9C" }} />,
      "Congratulations",
      `${userName}, You've qualified for the next interview round.`,
      "success",
      "success_container"
    );

  const renderNotQualifiedForNextRound = () =>
    renderAlert(
      <CheckCircleFilled style={{ marginRight: 8 }} />,
      "Thank you!",
      `${userName}, Thank you for completing the online assessment. Our team will assess your profile and get back to you soon.`,
      "error",
      "error_container"
    );

  if (
    isRoundFinished &&
    isLastRound &&
    currentInterviewSession.status === "pending"
  ) {
    if (appliedJob.status === "hired" || appliedJob.status === "rejected") {
      return <ApplicationUnderReview />;
    }
  }

  return (
    <Flex justify="center" align="center" style={{ flexDirection: "column" }}>
      {!isRoundFinished && appliedJob?.status === "applied" ? (
        <>
          <Flex
            align="center"
            style={{ width: "100%", justifyContent: "center" }}
          >
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={onNavigateToQuestions}
              style={{ cursor: "pointer" }}
            />
            <Typography.Title level={4} style={{ margin: "0 16px" }}>
              {`Answered: ${answeredQuestionCount}/${questions.length}`}
            </Typography.Title>
            <Button
              type="text"
              icon={<ArrowRightOutlined />}
              style={{ cursor: "pointer" }}
              disabled
            />
          </Flex>
          <Typography.Paragraph style={{ textAlign: "center" }}>
            Ready to evaluate your answers? Please ensure all the questions are
            answered and submit for evaluation.
          </Typography.Paragraph>
          <Divider />
          <Button
            type="primary"
            onClick={handleEvaluation}
            disabled={!isAnyOneQuestionAnswered}
            loading={isLoading}
          >
            Evaluate Response
          </Button>
        </>
      ) : (
        <>
          {currentInterviewSession?.status === "qualified" &&
            renderSuccessMessage()}
          {currentInterviewSession.status === "unqualified" &&
            renderNotQualifiedForNextRound()}
        </>
      )}
    </Flex>
  );
};
export default Evaluate;
